import type { ConfigInput } from "./config";
import type { Cents } from "./money";
import type { InvoiceLineBreakdown } from "./pricing";
import type { ProductType } from "./product";

/**
 * Order lifecycle for the half-automatic dropship flow (BLUEPRINT §7).
 * Orders are created server-side after Mollie confirms `paid`.
 */
export type OrderStatus =
  | "offerte"
  | "awaiting_payment"
  | "paid"
  | "te_beoordelen"
  | "approved"
  | "ordered_at_supplier"
  /** Timmers heeft de bestelling aangenomen en een leverdatum toegezegd (06-08-2026).
   *  Zonder deze stap zag je na het versturen van de bestelbon niet of de leverancier 'm
   *  had opgepakt — bij een order die stilviel wist je niet waar het haperde. */
  | "confirmed_by_supplier"
  | "shipped"
  | "completed"
  | "cancelled"
  | "refunded";

export interface Address {
  street: string;
  postalCode: string;
  city: string;
  country?: string;
}

/**
 * Bezorgmethode: bezorgen (standaard, met verzendkosten) of gratis afhalen op locatie.
 * Ontbreekt op oude orders → gedraagt zich als "ship".
 */
export type DeliveryMethod = "ship" | "pickup";

/**
 * Vaste afhaallocatie (gratis afhalen). Gedeeld door checkout, admin, e-mails én de
 * levertijden-pagina — één bron, zodat adres en openingstijden nooit uiteen kunnen lopen.
 *
 * LET OP: afhalen kan ALLEEN in Oosterhout. Op de oude site stond ook Amersfoort; dat is
 * vervallen en leverde klantvragen op. Amersfoort is het kantoor-/postadres, geen afhaalpunt.
 * Openingstijden overgenomen van timmers-bv.nl (gecontroleerd 2026-07-25) — wijzigen ze daar,
 * pas ze dan hier aan.
 */
export const PICKUP_LOCATION = {
  name: "Glastotaal — Oosterhout",
  street: "Krombraak 8-10",
  postalCode: "4906 CR",
  city: "Oosterhout",
  phone: "0162 461175",
  /** Openingstijden voor het afhalen, per dagengroep. */
  hours: [
    { days: "Maandag t/m donderdag", time: "8:00 – 17:00 uur" },
    { days: "Vrijdag", time: "8:00 – 16:00 uur" },
    { days: "Zaterdag en zondag", time: "gesloten" },
  ],
} as const;

export interface CustomerDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  /** B2B fields. */
  companyName?: string;
  kvk?: string;
  vatNumber?: string;
  /** Bezorgen (standaard) of gratis afhalen in Oosterhout. Afwezig = "ship". */
  deliveryMethod?: DeliveryMethod;
  /** Bezorgadres (niet nodig bij afhalen). */
  address?: Address;
  /** Factuuradres — only when it differs from the bezorgadres. */
  billingAddress?: Address;
}

/** Immutable snapshot of one configured/selected item as it was ordered. */
export interface OrderItem {
  productId: string;
  productName: string;
  /**
   * ONS artikelnummer (GT-…), zoals de klant het op de productpagina zag — vastgelegd bij het
   * aanmaken van de order, niet naslaan. Een nummer verschuift zodra Timmers artikelen toevoegt
   * of verwijdert (de doornummering is per categorie), en dan wijst een oude factuur naar iets
   * anders dan er besteld is. Het Timmers-nummer blijft in `internalNote` staan: dat is inkoop.
   */
  articleNumber?: string;
  type: ProductType;
  quantity: number;
  /** True for true made-to-measure items (no right of withdrawal). */
  isCustom: boolean;

  /**
   * GA4-identiteit van deze regel (zie ga4-item.ts): dezelfde item_id/item_name die de browser
   * bij bekijken en in de winkelwagen stuurde. De serverzijdige aankoop gebruikt deze, zodat
   * bekijken en kopen in de GA4-productrapporten op dezelfde regel uitkomen.
   */
  gaItemId?: string;
  gaItemName?: string;
  /** GA4 item_category, vastgelegd bij het aanmaken (zie ga4-item.ts). */
  gaItemCategory?: string;

  // configurable snapshot
  variantId?: string;
  netWidthMm?: number;
  netHeightMm?: number;
  shapeId?: string;
  edgeFinishId?: string;

  /**
   * Gekozen lengte (mm) bij een op-lengte-artikel — het getal waar de prijs op berust.
   * Stond eerder alleen in de vrije notitietekst; dan kun je een bedrag niet narekenen en valt
   * het niet op als de prijs bij een andere lengte hoort dan de bon vermeldt (order 1035).
   */
  lengthMm?: number;

  /** Per-unit price excl. BTW, in cents. */
  unitPrice: Cents;
  /**
   * Wat WIJ per stuk aan de leverancier betalen (netto inkoop, excl. btw) — vastgelegd bij het
   * aanmaken van de order.
   *
   * Waarom bewaren en niet naslaan: bij een configureerbaar product (ventilatierooster,
   * douche-opstelling, voorzetraam) staat er geen prijs in de catalogus — die hangt van de maat en
   * de opties af. De bestelbon pakte dan `priceCents` uit de catalogus, en dat is voor die 54
   * artikelen 0. Zo kwam er een bestelbon van bijna niets naar Timmers (order 1006, 13-08-2026).
   */
  supplierUnitCost?: Cents;
  /**
   * Waar die inkoopprijs uit is opgebouwd, als het product uit onderdelen bestaat (douche-
   * opstelling: glasdelen + beslag). Elk onderdeel heeft bij Timmers zijn eigen dealerkorting, dus
   * één percentage over het geheel klopt nooit. De bestelbon zet ze als subregels onder de regel,
   * zodat de leverancier ziet wat hij precies moet leveren en jij de factuur kunt narekenen.
   */
  supplierParts?: { label: string; count: number; unitCost: Cents; articleNumber?: string }[];
  /** unitPrice × quantity, excl. BTW, in cents. */
  linePrice: Cents;
  /** Vrije spec-notitie (gekozen kleur/maat/opties van een configureerbaar artikel). Deze regel
   *  ziet de KLANT: op de factuur, in de bevestigingsmail en in zijn account. */
  note?: string;
  /**
   * Notitie voor onszelf: inkoopreferentie (leveranciersnaam + hun artikelnummer), controlepunten
   * bij een zelf opgegeven scharnier, enzovoort. Staat alleen in de admin en op de bestelbon naar
   * de leverancier — nooit op de factuur of in een klantmail. Bij wie we inkopen is onze zaak.
   */
  internalNote?: string;
  /**
   * Prijsopbouw per regel (glas + randafwerking + bewerkingen apart), opgeslagen bij het
   * aanmaken van de order zodat de factuur én de bevestigingsmail exact dezelfde uitsplitsing
   * tonen. Alleen aanwezig op nieuwe glas-op-maat-orders.
   */
  breakdown?: InvoiceLineBreakdown;
  /**
   * Volledige configuratie-snapshot (maten + alle bewerkingen) voor glas op maat.
   * Nodig om de Timmers-doorzetfactuur (per-bewerking-breakdown) te reconstrueren.
   * Alleen aanwezig op nieuwe glas-orders.
   */
  config?: ConfigInput;
  /** Gekozen geslepen randlengte (mm) bij per-rand slijpen — voor de breakdown. */
  grindLengthMm?: number;
  /** Geserialiseerde maatvoering-tekening (SVG) — getoond op de factuur/bestelbon. */
  drawingSvg?: string;
  /** Bewerkingen mét coördinaten voor de productietekening (zie ProductionOp). */
  productionOps?: ProductionOp[];
  /** Prijs-op-aanvraag-regel: prijs volgt in de offerte (≠ een echt €0-geprijsd artikel). */
  onRequest?: boolean;
}

/**
 * A customer's right-of-withdrawal request (ACM ontbindingsknop, 19-6-2026).
 * Only covers non-custom items; made-to-measure glass is excluded by law.
 */
export interface WithdrawalRequest {
  /** Epoch milliseconds the request came in. */
  requestedAt: number;
  status: "requested" | "processed";
  /** productIds of the herroepbare (non-custom) items the request covers. */
  itemProductIds: string[];
  /** Epoch milliseconds the admin marked it handled/refunded. */
  processedAt?: number;
}

/** Een (per-klant) korting die op de bestelling wordt verrekend. */
export interface OrderDiscount {
  kind: "percent" | "amount";
  /** Percentage (1–100) of een vast bedrag in centen (excl. btw). */
  value: number;
  note?: string;
}

export interface OrderTotals {
  /** Sum of line prices excl. BTW, in cents. */
  subtotal: Cents;
  /** Verrekende korting op het goederen-subtotaal, excl. BTW, in cents (indien van toepassing). */
  discount?: Cents;
  /** Delivery cost excl. BTW, in cents. */
  shipping: Cents;
  vat: Cents;
  /** subtotal − discount + shipping + vat, in cents. */
  total: Cents;
}

/** A glas-op-maat line: the server recomputes the price from the ConfigInput. */
export interface GlassOrderLine {
  config: ConfigInput;
  /** UI-selected ground-edge length (mm) for per-edge slijpen. */
  grindLengthMm?: number;
  /** Geserialiseerde maatvoering-tekening (SVG) voor op de factuur/bestelbon. */
  drawingSvg?: string;
  /** Bewerkingen mét coördinaten voor de productietekening (zie ProductionOp). */
  productionOps?: ProductionOp[];
}

/** A fixed-price article line: the server looks up the price from the catalog. */
export interface ArticleOrderLine {
  articleSlug: string;
  quantity: number;
  /** Vrije spec-notitie (bv. gekozen kleur/maat van een configureerbaar artikel). */
  note?: string;
  /** Op-lengte-artikel (U-profiel): gewenste lengte in mm; server prijst per meter. */
  lengthMm?: number;
  /** Voorzetraam (STORM_WINDOW): nominale maat; server herprijst basis + grootte-/energietoeslag. */
  wMm?: number;
  hMm?: number;
  /** Offerte-regel (prijs op aanvraag): server rekent €0 in offerte-modus, geen prijs-lookup. */
  quote?: boolean;
  /** Naam voor op de order (offerte-items zonder catalogus-artikel, bv. BENGglas/rooster). */
  name?: string;
}

/** A BENGglas (LandVac vacuümglas) line: priced by the server from the productId + dims. */
export interface BenglasOrderLine {
  benglasProductId: string;
  widthMm: number;
  heightMm: number;
  quantity: number;
  shape?: string;
  innerPane?: string;
  /** Spouwmaat (mm) bij hybride binnenruit — alleen spec, geen prijsinvloed. */
  spouwMm?: number;
  /** Afstandhouder in die spouw ("standaard" | "warm-edge") — ook alleen spec. */
  spacer?: string;
}

/** A ventilatierooster line: priced by the server from the Duco staffel (breedte × kleurklasse + bediening). */
export interface RoosterOrderLine {
  /** Basis-artikel-slug van het rooster (sleutel in de server-prijsstaffel). */
  roosterSlug: string;
  widthMm: number;
  /** Niet-RAL afwerking (DAR/F1/…). Leeg wanneer een RAL-kleur is gekozen. Bij BiColour = BUITENkleur. */
  colorType?: string;
  /** Gekozen RAL-code (bv. "9010") wanneer een RAL-kleur is gekozen; server leidt de prijsklasse af. Bij BiColour = BUITENkleur. */
  ral?: string;
  /** BiColour: aparte BINNENkleur (niet-RAL afwerking). Aanwezig ⇒ bicolor; buiten = colorType/ral hierboven. */
  colorTypeBinnen?: string;
  /** BiColour: aparte BINNEN-RAL-code. Aanwezig ⇒ bicolor. Prijs = de duurste van buiten/binnen. */
  ralBinnen?: string;
  /** Optionele bediening (vast bedrag per stuk). */
  bediening?: "koord" | "stang";
  /** Aan welke zijde(n) de bediening zit; "beide" = ×2. */
  zijde?: "een" | "beide";
  /**
   * Bij stangbediening: kleur en lengte van de bedieningsstang. De klant kiest ze in de
   * configurator, maar ze stonden niet in deze regel — waardoor de server ze niet kende en ze
   * dus op de bestelbon naar Timmers én op de orderbevestiging ontbraken. Zonder die twee kan
   * de leverancier de stang niet uitleveren (13-08-2026).
   */
  stangColor?: string;
  /** Lengte van de bedieningsstang in mm (Duco: 750 of 1000). */
  stangLengthMm?: number;
  /** Glasgoot-breedte (mm) — cosmetisch, voor de notitie. */
  gutter?: number;
  /** Plaatsingswijze: in de glassponning ("glas") of op een dicht paneel/kalf ("kalf"). Prijs-neutraal. */
  plaatsing?: "glas" | "kalf";
  quantity: number;
}

/** An isolatieglas line: priced by the server from the buildup (pakket+outer+inner) + dims. */
export interface IsolatieglasOrderLine {
  isoPakket: string;
  isoOuter: string;
  isoInner: string;
  isoMiddle?: string;
  isoShapeId?: string;
  isoGas?: string;
  isoSpacer?: string;
  /** UV-bestendige siliconen randafdichting (toeslag per m1 omtrek). */
  isoSiliconen?: boolean;
  widthMm: number;
  heightMm: number;
  quantity: number;
}

/** A douche-opstelling line: priced by the server via calculateShowerPrice (glas + beslag). */
export interface ShowerOrderLine {
  showerLayout: string;
  showerFinish: string;
  showerMatGlass?: boolean;
  showerGlassColor?: string;
  showerDepthMm?: number;
  showerHardware?: {
    scharnier?: string; knop?: string; stang?: string;
    uProfiel?: string; sealDoor?: string; sealHoek?: string;
  };
  heightMm: number;
  /** Breedte per glasdeel, volgorde = SHOWER_LAYOUTS[].panels (deur eerst). */
  widthsMm: number[];
  quantity: number;
}

/** One line the client asks the server to price + order. IDs + dims only, no amounts. */
export type OrderLineRequest = GlassOrderLine | ArticleOrderLine | BenglasOrderLine | IsolatieglasOrderLine | RoosterOrderLine | ShowerOrderLine;

/** True when a line is a fixed-price article (vs. glas op maat). */
export function isArticleLine(line: OrderLineRequest): line is ArticleOrderLine {
  return "articleSlug" in line;
}

/** True when a line is an isolatieglas buildup. */
export function isIsolatieglasLine(line: OrderLineRequest): line is IsolatieglasOrderLine {
  return "isoPakket" in line;
}

/** True when a line is a configureerbaar ventilatierooster. */
export function isRoosterLine(line: OrderLineRequest): line is RoosterOrderLine {
  return "roosterSlug" in line;
}

/** True when a line is a BENGglas (vacuümglas) configuratie. */
export function isBenglasLine(line: OrderLineRequest): line is BenglasOrderLine {
  return "benglasProductId" in line;
}

/** True when a line is a douche-opstelling. */
export function isShowerLine(line: OrderLineRequest): line is ShowerOrderLine {
  return "showerLayout" in line;
}

/** Checkout payload: the server recomputes every price from trusted data. */
export interface CreateOrderRequest {
  lines: OrderLineRequest[];
  customer: CustomerDetails;
  /** True = offerte-aanvraag (bevat prijs-op-aanvraag-items): geen online betaling,
   *  order krijgt status "offerte" en wij sturen later één betaallink. */
  quote?: boolean;
  /** Montage-doorverwijzing naar een partner-glaszetter (ProGlass): altijd een offerte, geen
   *  betaling/betaallink aan ons; wij sturen de aanvraag automatisch door en ontvangen commissie. */
  referral?: "proglass";
  /** Ingetikte kortingscode. De server bepaalt of hij geldt en wat hij oplevert. */
  couponCode?: string;
  /** GA4 client-/sessie-id van deze bezoeker — de server meet de aankoop namens hem. */
  ga4ClientId?: string;
  ga4SessionId?: string;
  /** Toestemming analytische cookies bij het afrekenen; false = aankoop anoniem meten. */
  consentAnalytics?: boolean | null;
  /** Google-klik-ID van de advertentie waarlangs deze bezoeker binnenkwam (gclid/gbraid/wbraid),
   *  alleen aanwezig met marketing-toestemming. Zie `src/lib/click-id.ts`. */
  clickId?: string;
  clickIdType?: "gclid" | "gbraid" | "wbraid";
  /** Tijdstip van die advertentieklik (epoch ms) — Google wil 'm bij een offline import erbij. */
  clickAt?: number;
  /** Vrije toelichting bij een offerte-aanvraag (situatie, maten, wensen). */
  customerNote?: string;
  /** Bij een offerte-aanvraag meegestuurde foto-URL's (Firebase Storage). */
  photos?: string[];
  /** De levertijd die in de winkelwagen/kassa stond — zie `Order.promisedLeadTime`. De server
   *  neemt hem alleen over als hij in de admin-instellingen voorkomt. */
  promisedLeadTime?: string | null;
}

/**
 * Eén bewerking zoals productie 'm moet uitvoeren, met de maten waarmee de machine werkt.
 *
 * De prijsregels (`ProcessingRequest`) kennen alleen soort + maat + aantal — genoeg om te
 * rekenen, te weinig om te maken. Voor de productietekening leggen we daarom per gat en per
 * uitsparing de plek vast: X en Y vanaf de referentiehoek (linksonder, net als op de
 * Timmers-tekening), de diameter of B×H, de straal van een afronding en of de rand geslepen is.
 * Puur informatief: de prijs komt onveranderd uit `config.processings`.
 */
// Let op: de Firebase-callable maakt van een weggelaten veld `null`. Vandaar overal `| null`.
export interface ProductionOp {
  kind: "boring" | "uitsparing" | "hoek";
  /** Omschrijving zoals op de bon ("Boring", "Uitsparing", "Ronde hoek"). */
  label: string;
  /** Hoekpositie in woorden: "linksonder", "rechtsboven", … */
  positie?: string | null;
  /** Hart-X vanaf de LINKER rand (mm). */
  xMm?: number | null;
  /** Hart-Y vanaf de ONDERrand (mm). */
  yMm?: number | null;
  /** Boring: diameter (mm). */
  diaMm?: number | null;
  /** Uitsparing: breedte × hoogte (mm). */
  wMm?: number | null;
  hMm?: number | null;
  /** Hoek/uitsparing-afronding: straal (mm). */
  radiusMm?: number | null;
  /** Hart-op-hart tot de volgende boring (mm) — zoals "d:152" op de tekening. */
  hohMm?: number | null;
  /** Randafwerking van deze bewerking: "geslepen" of "ongeslepen". */
  slijpen?: string | null;
  /** Aantal (bij hoeken: hoeveel hoeken). */
  aantal?: number | null;
}

/**
 * Het ordernummer dat de KLANT ziet.
 *
 * Sinds 06-08-2026 een oplopend getal vanaf 1001 (`orderNumber` op de order, toegekend door
 * createOrder via de teller in `counters/orders`). Daarvóór was het de eerste acht tekens van
 * de Firestore-doc-id — een reeks als "IOASPVJE", die niemand aan de telefoon kan voorlezen en
 * waaraan je niet ziet welke bestelling ouder is.
 *
 * De terugval op die oude vorm blijft staan: bestellingen van vóór de omslag hebben geen
 * `orderNumber` en moeten hun bestaande nummer houden — dat staat op facturen en in mails die
 * al bij klanten liggen.
 */
export function orderLabel(order: { id: string; orderNumber?: number }): string {
  return order.orderNumber != null ? String(order.orderNumber) : order.id.slice(0, 8).toUpperCase();
}


/**
 * Het nummer dat op het document staat: het factuurnummer als er betaald is, anders het
 * bestelnummer. Eén plek, zodat de factuur-HTML, de mail, de PDF-bijlage en de admin nooit een
 * ander nummer tonen voor dezelfde bestelling.
 */
export function stukLabel(order: { id: string; orderNumber?: number; invoiceNo?: string }): string {
  return order.invoiceNo ?? orderLabel(order);
}

/** Zelfde regel, maar met de losse velden — voor plekken die alleen een id (+ nummer) hebben. */
export function orderNumber(id: string, nummer?: number): string {
  return nummer != null ? String(nummer) : id.slice(0, 8).toUpperCase();
}

/** What `createOrder` returns: where to send the customer to pay. */
export interface CreateOrderResult {
  orderId: string;
  /**
   * Het nette volgnummer (1007, 1008, …). Nodig omdat de bedankpagina anders het rauwe
   * Firestore-document-id toonde ("psxVUUvSb5op1QY8vy0e") terwijl de bevestigingsmail en de admin
   * wél het volgnummer gebruiken — dezelfde bestelling had zo twee verschillende "nummers".
   * Ontbreekt alleen als de teller faalde; `orderNumber()` valt dan terug op de id-vorm.
   */
  orderNumber?: number;
  /** Mollie hosted-checkout URL. Null in stub mode (no Mollie key configured). */
  checkoutUrl: string | null;
  /** True when no real Mollie payment was created (local/dev stub). */
  stub: boolean;
}

export interface Order {
  id: string;
  /**
   * Oplopend bestelnummer — wat de klant en Timmers zien (zie `orderLabel`). 1001 t/m 1111,
   * daarna vijf cijfers vanaf 10001 zodat het niet op een factuurnummer (F-1112) lijkt.
   * Ontbreekt op orders van vóór 06-08-2026; die vallen terug op de eerste acht tekens van `id`.
   */
  orderNumber?: number;
  /**
   * Factuurnummer, zoals het op papier staat: `F-2026001`. Een **eigen reeks per jaar**, die
   * alleen optelt als er betaald wordt — dus zonder gaten, ook al wordt niet elke bestelling
   * betaald. (Van de eerste 108 bestellingen bleven er 25 onbetaald.)
   *
   * Bewust een heel andere vorm dan het bestelnummer (1112, 1113, …). Die twee stonden eerder
   * dicht bij elkaar — bestelling 1114 naast factuur F-1113 — en dat is aan de telefoon niet uit
   * elkaar te houden. Nu kan dat niet meer.
   *
   * Opgeslagen als tékst en niet als getal: een factuurnummer is een kenmerk, geen bedrag. En het
   * ligt vast zodra het is toegekend — facturen van vóór 27-10-2026 dragen hun oude nummer (het
   * bestelnummer), en daar schrijf je niet overheen.
   *
   * Ontbreekt zolang er niet betaald is: dan is het document een bestelbon, geen factuur.
   */
  invoiceNo?: string;
  /** Wanneer het factuurnummer is toegekend (epoch ms). Het factuurmoment, niet het betaalmoment. */
  invoicedAt?: number;
  items: OrderItem[];
  /**
   * True when the order contains any made-to-measure line. Drives the
   * withdrawal-right split (ACM herroepingsknop, 19-6-2026).
   */
  isCustom: boolean;
  customer: CustomerDetails;
  /** Firebase Auth uid when placed by a signed-in (B2B) customer. */
  customerUid?: string;
  totals: OrderTotals;
  /** Toegepaste klantkorting (snapshot), voor weergave op order/factuur. */
  discount?: OrderDiscount;
  /** GA4 client-id van de bezoeker die afrekende. Nodig om de aankoop die de SERVER meet (na de
   *  betaalbevestiging van Mollie) aan dezelfde bezoeker en dus aan het juiste kanaal te hangen. */
  ga4ClientId?: string;
  /** GA4 sessie-id van datzelfde bezoek — houdt de aankoop binnen die sessie. */
  ga4SessionId?: string;
  /** Toestemming analytische cookies bij het afrekenen; false = aankoop anoniem meten. */
  consentAnalytics?: boolean | null;
  /** Wanneer het team de offerteprijs vastzette (epoch ms) — scheidt "nieuw" van "geprijsd". */
  quotePricedAt?: number;
  /** Wie dat deed (e-mail of uid), zodat je bij een verkeerde prijs weet wie je moet hebben. */
  quotePricedBy?: string;
  /** Wanneer de offerte met betaallink de deur uit ging (epoch ms). */
  quoteSentAt?: number;
  /** Wanneer het purchase-event naar GA4 ging (epoch ms). Voorkomt dubbel meten bij een
   *  webhook-retry van Mollie. */
  ga4PurchaseSentAt?: number;
  /** Moment waarop Mollie de betaling bevestigde (epoch ms). Conversietijdstip voor de
   *  Ads-import; ontbreekt bij orders van vóór 15-08-2026. */
  paidAt?: number;
  /** Google-klik-ID van de advertentie waarlangs deze klant binnenkwam. Hiermee kan een betaalde
   *  bestelling als offline conversie bij Google Ads geïmporteerd worden — óók als de klant nooit
   *  op de bedankpagina terugkwam en de browser-conversie dus nooit vuurde. */
  clickId?: string;
  clickIdType?: "gclid" | "gbraid" | "wbraid";
  clickAt?: number;
  /** Wanneer deze order in een Ads-exportbestand is meegenomen (epoch ms) — voorkomt dat je 'm
   *  bij een volgende export nóg een keer uploadt en de conversie dubbel telt. */
  adsExportedAt?: number;
  /** Ingewisselde kortingscode (snapshot). Staat er ook als de code alléén de verzendkosten
   *  liet vervallen — dan is `discount` leeg en is `freeShipping` waar. */
  coupon?: {
    code: string;
    kind: "percent" | "amount" | "freeShipping";
    /** Wat de code deze klant bespaarde, excl. btw (centen). */
    savingCents: Cents;
    freeShipping?: boolean;
  };
  status: OrderStatus;
  /** Gezet bij een montage-doorverwijzing naar ProGlass: geen eigen betaling/factuur — de partner
   *  offreert en factureert de klant; wij tracken dit voor de commissie (geen betaallink-knop). */
  referral?: "proglass";
  /** Vrije toelichting van de klant bij een offerte-aanvraag (situatie, wensen). */
  customerNote?: string;
  /** Door de klant meegestuurde foto-URL's bij een offerte-aanvraag. */
  photos?: string[];
  molliePaymentId?: string;
  /** Mollie hosted-checkout URL (bv. de handmatig gestuurde betaallink bij een offerte). */
  checkoutUrl?: string;
  /**
   * Met de hand in de admin aangemaakte offerte (telefonisch/per mail binnengekomen).
   *
   * Waarom dit vastligt: bij zo'n offerte zijn de regels vrije tekst, dus die mag je achteraf
   * herschrijven, splitsen of aanvullen. Bij een offerte die via de site binnenkwam staan er
   * échte configuraties onder — daar een regel bijzetten of hernoemen zou betekenen dat de
   * server iets anders herberekent dan de klant heeft samengesteld.
   */
  manualQuote?: boolean;
  /** Interne notities van het team (niet zichtbaar voor de klant). */
  notes?: OrderNote[];
  /** Ge-uploade documenten bij deze bestelling (orderbevestiging, leveranciersfactuur, pakbon …). */
  documents?: OrderDocument[];
  /** Opgemaakte creditnota's bij deze bestelling (kan er meer dan één zijn). */
  credits?: CreditNote[];
  /** Bezorg-/track&trace-info, ingevoerd door het team. */
  tracking?: OrderTracking;
  /** Epoch ms waarop de verzend-mail (track&trace) naar de klant is gestuurd — voorkomt dubbele mails. */
  shipmentNotifiedAt?: number;
  /** Right-of-withdrawal request, when a customer used the herroepingsknop. */
  withdrawal?: WithdrawalRequest;
  /** Epoch ms waarop de bestelbon naar Timmers (leverancier) is gemaild — badge + dubbele-bestel-guard. */
  timmersOrderedAt?: number;
  /** E-mailadres van de beheerder die de Timmers-bestelling verstuurde. */
  timmersOrderedBy?: string;
  /**
   * Hoe deze bestelling naar de klant gaat, vastgelegd bij het aanmaken.
   *
   * Waarom vastleggen en niet elke keer afleiden: de server kent bij het aanmaken de échte
   * regels (glas op maat, voorzetraam, glaslatten van 5 meter, BENGglas-speciaaltransport) en
   * rekent het transporttarief daarop. Een scherm dat het later opnieuw uit de opgeslagen
   * regels probeert te raden, raadt anders. Zo stond bij een bestelling met alleen een rolmat
   * "Eigen-transportroute: donderdag", terwijl die gewoon met de post gaat.
   *
   * - "glass_transport" : eigen glastransport van de leverancier (vaste bezorgdag per regio)
   * - "benglas"         : BENGglas-speciaaltransport (kan niet afgehaald worden)
   * - "parcel"          : pakketdienst
   * - "pickup"          : klant haalt op in Oosterhout
   */
  shippingMethod?: "glass_transport" | "benglas" | "parcel" | "pickup";
  /**
   * Btw-behandeling van deze order, met het bewijs erbij. Bij een intracommunautaire levering is
   * `tarief` 0 en hoort er "Btw verlegd" op de factuur; `reden` legt in gewone taal uit waarom.
   * Ontbreekt dit veld (orders van vóór 03-09-2026), dan gold het Nederlandse tarief.
   */
  btw?: {
    tarief: number;
    verlegd: boolean;
    status: string;
    reden: string;
    vatNumber?: string;
    /** Wat VIES antwoordde. Ontbreekt = niet gecontroleerd. */
    viesGeldig?: boolean;
    viesNaam?: string;
    /** VIES lag eruit bij het bestellen → nalopen zodra hij weer antwoordt. */
    viesOnbereikbaar?: boolean;
    viesGecheckAt?: number;
  };

  /**
   * De levertijd die de klant bij het afrekenen te zien kreeg ("2-3 werkdagen", "1-3 weken").
   *
   * Waarom vastleggen en niet achteraf opnieuw bepalen: de statusmail rekende de levertijd zelf
   * uit en kwam dan bij het grove veld `articlesLeadTimeText` uit — "2-3 werkdagen" — óók als het
   * artikel bij Timmers besteld moest worden en de site "1-3 weken" had beloofd. De klant las dus
   * in de mail een vijf keer kortere levertijd dan op de site stond, en ging bellen zodra die
   * drie dagen voorbij waren. De order bewaart nu wat er beloofd ís; de mail herhaalt dat.
   *
   * De browser stuurt de tekst mee, maar de server neemt hem alleen over als hij ook écht in de
   * admin-instellingen staat — anders zou een klant zijn eigen levertijd kunnen invullen.
   */
  promisedLeadTime?: string;

  /** Epoch ms waarop Timmers de orderbevestiging stuurde (status → confirmed_by_supplier). */
  supplierConfirmedAt?: number;
  /** Leverdatum die Timmers in hun bevestiging toezegt (epoch ms, dagnauwkeurig).
   *  Vastleggen zodat je kunt zien welke orders over tijd zijn — en een klant die belt een
   *  concreet antwoord krijgt in plaats van "het duurt 2-3 weken". */
  supplierPromisedDate?: number;
  /**
   * Afhaalorder: de dag waarop de bestelling in Oosterhout klaarligt (epoch ms, dagnauwkeurig).
   *
   * Waarom een datum en niet alleen een statusknop: de oude flow vroeg om op de dág van klaarliggen
   * op "Klaar om af te halen" te drukken. Dat betekent per order een datum uit de orderbevestiging
   * onthouden en er dagen later op terugkomen — bij een handvol orders gaat dat mis. Nu vult de
   * admin de datum uit de OB meteen in en krijgt de klant hem direct te horen.
   */
  pickupReadyDate?: number;
  /** Epoch ms waarop we de afhaaldatum voor het laatst naar de klant mailden. Aanwezig = de klant
   *  kent al een datum, dus een wijziging daarna is een wíjziging en krijgt een andere mail. */
  pickupDateNotifiedAt?: number;
  /** Epoch ms waarop de bestelling is afgerond/bezorgd (status → completed) — basis voor het reviewverzoek. */
  completedAt?: number;
  /** Epoch ms waarop het reviewverzoek is verstuurd — voorkomt dubbele review-mails. */
  reviewRequestedAt?: number;
  /** Epoch ms waarop de "betaling niet afgerond"-herinnering is gestuurd — één reminder per order. */
  paymentReminderSentAt?: number;
  /** Laatste keer dat de klant zelf "verder betalen" koos op een nog onbetaalde bestelling.
   *  Scheelt bij navragen: je ziet of iemand het geprobeerd heeft of nooit terugkwam. */
  paymentResumedAt?: number;
  /**
   * Laatste keer dat een betaling bij Mollie verliep zonder dat er betaald is.
   *
   * Vervangt het stilletjes annuleren van de order. Een iDEAL-betaling verloopt na een kwartier,
   * en dat betekende tot 21-10-2026: één herinnering, en bij de volgende afloop status
   * `cancelled`. Een handmatige offerte van € 895 was daardoor 35 minuten na het versturen van de
   * betaallink uit de bestellijst verdwenen, zonder mail of notitie (order 1098, Taco kon hem
   * nergens meer vinden). Nu blijft de order staan op "wacht op betaling" en zie je hier wanneer
   * de laatste poging verliep.
   */
  paymentExpiredAt?: number;

  /** Wanneer de factuur voor het laatst vanuit de admin verstuurd is, naar welk adres, en hoe
   *  vaak in totaal. Zo is achteraf te zien dát hij de deur uit is — en waarheen. */
  /** Waarom de bestelling is afgezegd of terugbetaald — ingevuld door de admin. */
  cancelReason?: string;

  invoiceSentAt?: number;
  invoiceSentTo?: string;
  invoiceSentCount?: number;
  /** Epoch milliseconds. */
  createdAt: number;
}

/**
 * Documentsoorten die we per bestelling bewaren. De volgorde is die van het inkoopproces:
 * wat we vragen → wat we bestellen → wat de leverancier bevestigt → wat er geleverd wordt →
 * wat er betaald wordt → wat er daarna nog gebeurt. Een boekhouder of controleur kan zo van
 * boven naar beneden lezen.
 *
 * "schade" stond hier eerder als schade-/klachtdossier, maar werd in de praktijk niet gebruikt;
 * offertes hadden juist geen plek. Omgezet toen er nog geen enkel schade-document bestond, dus
 * zonder achterlatende documenten met een soort die niemand meer kent.
 */
export const ORDER_DOC_TYPES = [
  // Vóór de orderbevestiging, want een offerte gaat aan de bestelling vooraf.
  "offerte",
  "orderbevestiging",
  // De afleverbevestiging hoort direct ná de orderbevestiging: dat is de volgorde waarin het
  // gebeurt (leverancier bevestigt → er wordt geleverd → hun factuur komt).
  "pakbon",
  "leveranciersfactuur",
  "productietekening",
  "correspondentie",
  "retour",
  "overig",
] as const;

export type OrderDocType = (typeof ORDER_DOC_TYPES)[number];

export const ORDER_DOC_LABEL: Record<OrderDocType, string> = {
  offerte: "Offerte",
  orderbevestiging: "Orderbevestiging leverancier",
  pakbon: "Afleverbevestiging",
  leveranciersfactuur: "Factuur van de leverancier",
  productietekening: "Goedgekeurde productietekening",
  correspondentie: "Correspondentie met de klant",
  // Het sleutelwoord blijft `retour` (bestaande documenten verwijzen ernaar); alleen het label
  // op het scherm is aangepast (Thijs, 03-10-2026).
  retour: "Retourformulier",
  overig: "Overig",
};

/** Eén bewaard document bij een bestelling. Het bestand zelf staat in Firebase Storage. */
export interface OrderDocument {
  /** Willekeurige id — ook de basis van de bestandsnaam in Storage. */
  id: string;
  type: OrderDocType;
  /** Bestandsnaam zoals geüpload (voor herkenning in de lijst). */
  fileName: string;
  /** Pad in Storage, bv. order-docs/<orderId>/<id>.pdf — nodig om te verwijderen. */
  path: string;
  /** Download-URL (token-URL van Storage). */
  url: string;
  contentType: string;
  sizeBytes: number;
  /** Vrije toelichting, bv. een factuurnummer of "deellevering 2 van 3". */
  note?: string;
  uploadedAt: number;
  uploadedBy: string;
}

/**
 * Eén creditnota bij een bestelling.
 *
 * **Waarom een eigen document en geen aanpassing van de order.** De factuur die de klant heeft, en
 * die de boekhouder al verwerkt heeft, moet blijven zoals hij was. Een creditnota staat ernaast,
 * met een eigen nummer uit een eigen doorlopende reeks, en verwijst naar het factuurnummer. Daardoor
 * kunnen er er ook meerdere zijn: eerst één stuks retour, later nog twee.
 *
 * Bedragen staan hier **positief** opgeslagen — het zijn de bedragen die worden teruggegeven. Pas
 * op de nota zelf en in de boekhouding krijgen ze een minteken. Zo blijft optellen en vergelijken
 * met de order eenvoudig en kan er geen tekenfout in de opslag sluipen.
 */
export interface CreditNote {
  /** Willekeurige id, voor bewerken/verwijderen en als bestandsnaam. */
  id: string;
  /** Het creditnotanummer uit de eigen reeks, bv. "CN-0001". */
  number: string;
  /** Epoch ms waarop de nota is opgemaakt — dit is de creditnotadatum. */
  at: number;
  /** E-mail van de beheerder die hem opmaakte. */
  by: string;
  /** Waarom er gecrediteerd wordt; komt op de nota te staan. */
  reason: string;
  /** Welke regels, en hoeveel stuks daarvan. Verwijst met de index naar `Order.items`. */
  lines: CreditNoteLine[];
  /** Hoeveel van de verzendkosten (excl. btw) wordt terugbetaald; 0 = niets. */
  shippingExclCents: number;
  /** Bedragen van déze nota, met dezelfde prijsmotor als de order berekend. Positief. */
  totals: OrderTotals;
  /** Epoch ms waarop de nota naar de klant is gemaild. Ontbreekt = niet verstuurd. */
  emailedAt?: number;
  /** Epoch ms waarop het geld daadwerkelijk terug is. Ontbreekt = nog niet terugbetaald. */
  refundedAt?: number;
  /** Refund-id bij Mollie, als de terugbetaling via het systeem liep. */
  mollieRefundId?: string;
  /**
   * Epoch ms waarop een terugbetaling is ingezet maar nog niet bevestigd. Voorkomt dat twee
   * gelijktijdige verzoeken allebei bij Mollie terechtkomen. Blijft staan als er onderweg iets
   * misgaat — dat is een zichtbaar spoor, en beter dan een tweede storting.
   */
  refundPendingAt?: number;
}

/** Eén regel op een creditnota: een stukje van een orderregel. */
export interface CreditNoteLine {
  /** Index in `Order.items` — de regel waar dit bij hoort. */
  itemIndex: number;
  /** Aantal stuks dat gecrediteerd wordt (≥ 1, en samen met eerdere nota's ≤ het bestelde aantal). */
  quantity: number;
  /** Stuksprijs excl. btw, overgenomen van de orderregel zodat de nota niet meebeweegt met de catalogus. */
  unitPriceExclCents: number;
  /** Omschrijving, overgenomen van de orderregel. */
  productName: string;
}

/** Interne ordernotitie (team-only). */
export interface OrderNote {
  /**
   * Eigen sleutel, zodat een notitie later te bewerken of te verwijderen is. Notities van vóór
   * 24-09-2026 hebben er geen; die worden aangesproken op hun `at` (binnen één order uniek, want
   * twee notities kunnen niet in dezelfde milliseconde zijn opgeslagen). Gebruik `noteKey()`.
   */
  id?: string;
  at: number;
  by: string;
  text: string;
  /** Epoch ms van de laatste wijziging. Afwezig = nooit bewerkt. */
  editedAt?: number;
}

/** De sleutel waarmee een notitie wordt aangesproken — ook als hij nog geen `id` heeft. */
export function noteKey(note: Pick<OrderNote, "id" | "at">): string {
  return note.id ?? String(note.at);
}

/** Verzend-/track&trace-info bij een order. */
export interface OrderTracking {
  /** Vervoerder/methode, bv. "Transmission" of "Eigen transport". */
  carrier?: string;
  /** Track&trace-link (Transmission) of relevante URL. */
  url?: string;
  /** Vrije notitie, bv. tijdsblok of opmerking. */
  note?: string;
  at: number;
}

/**
 * Wat voor bestelling dit is, bepaald uit de regels zelf.
 *
 * Nodig omdat teksten die kloppen voor glas op maat onzin worden bij losse artikelen: een klant
 * die twee profielen bestelde kreeg "Je maten zijn gecontroleerd en goedgekeurd, je glas gaat in
 * productie" — er wordt niets gemaakt en er zijn geen maten. Eén functie, zodat elke mail en elk
 * scherm dezelfde beslissing neemt.
 *
 * - "maatwerk"  : alleen op maat gemaakt werk (glas, isolatieglas, douche)
 * - "artikelen" : alleen catalogusartikelen uit voorraad/bestelling
 * - "gemengd"   : allebei — dan geldt de langzaamste/voorzichtigste tekst
 */
export type OrderKind = "maatwerk" | "artikelen" | "gemengd";

export function orderKind(items: Pick<OrderItem, "type" | "isCustom">[] | undefined): OrderKind {
  if (!Array.isArray(items) || items.length === 0) return "artikelen";
  // Een configureerbaar product is altijd maatwerk; een artikel kan het zijn als het op maat
  // gemaakt is (bv. een op lengte gezaagd profiel) — vandaar isCustom als tweede signaal.
  const maat = items.some((i) => i.type === "configurable" || i.isCustom);
  const kant = items.some((i) => i.type === "article" && !i.isCustom);
  if (maat && kant) return "gemengd";
  return maat ? "maatwerk" : "artikelen";
}

/** Wat de admin/klant leest bij een transportmethode. */
export const SHIPPING_METHOD_LABEL: Record<NonNullable<Order["shippingMethod"]>, string> = {
  glass_transport: "Eigen glastransport",
  benglas: "BENGglas-speciaaltransport",
  parcel: "Pakketdienst",
  pickup: "Afhalen in Oosterhout",
};

/**
 * Transportmethode van een bestelling. Nieuwe orders hebben 'm opgeslagen (createOrder zet 'm
 * bij dezelfde beslissing die het tarief bepaalt). Voor orders van vóór die wijziging leiden we
 * 'm alsnog af uit de regels — zo goed als de opgeslagen gegevens toelaten.
 *
 * Let op: die afleiding is een terugval, geen tweede waarheid. Ziet een scherm iets anders dan
 * het tarief dat de klant betaald heeft, dan is de opgeslagen waarde leidend.
 */
export function orderShippingMethod(order: Pick<Order, "shippingMethod" | "items" | "customer">): NonNullable<Order["shippingMethod"]> {
  if (order.shippingMethod) return order.shippingMethod;
  if (order.customer?.deliveryMethod === "pickup") return "pickup";
  // Maatwerk met maten (glas, isolatieglas, douche, voorzetraam) gaat op de rekwagen.
  const opMaat = (order.items ?? []).some(
    (i) => i.type === "configurable" || (i.netWidthMm != null && i.netHeightMm != null),
  );
  return opMaat ? "glass_transport" : "parcel";
}
